import api from "@/lib/axiosConfig";
import { PermanentEnsemble } from "@/lib/types";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

const getPermanentEnsembles = async (): Promise<PermanentEnsemble[]> => {
  return api
    .get<PermanentEnsemble[]>("back/api/ensemble/permanent")
    .then(({ data }) => data);
};

export const useGetPermanentEnsembles = (
  options?: Omit<UseQueryOptions<PermanentEnsemble[]>, "queryKey" | "queryFn">,
) =>
  useQuery<PermanentEnsemble[]>({
    ...options,
    queryKey: ["ensembles", "permanent"],
    queryFn: getPermanentEnsembles,
  });

const updatePermanentEnsemble = async (data: PermanentEnsemble) => {
  const { id, ...payload } = data;

  return await api
    .put(`back/api/ensemble/permanent/${id}`, payload)
    .then(({ data }) => data);
};

export const useUpdatePermanentEnsemble = (
  options?: Omit<
    UseMutationOptions<PermanentEnsemble, Error, PermanentEnsemble>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updatePermanentEnsemble,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Ensemble fixe de l'instance (Réglages du CR), créé côté API s'il n'existe pas
 * encore — à appeler avant d'enregistrer les réglages d'une instance sans CR.
 */
const getOrCreateCrEnsemble = async (): Promise<PermanentEnsemble> =>
  api
    .post<PermanentEnsemble>("back/api/ensemble/permanent")
    .then(({ data }) => data);

export const useGetOrCreateCrEnsemble = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: getOrCreateCrEnsemble,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ensembles", "permanent"] });
    },
  });
};
